import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { CreditCard, ExternalLink, Banknote, RefreshCw, Loader2, AlertTriangle, Receipt } from "lucide-react";
import { toast } from "sonner";

const AdminPayments = () => {
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundFor, setRefundFor] = useState(null);
  const [refundReason, setRefundReason] = useState("requested_by_customer");
  const [refundAmount, setRefundAmount] = useState("");
  const [refunding, setRefunding] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [s, t, r] = await Promise.all([
        axios.get(`${API}/admin/payment-settings`),
        axios.get(`${API}/admin/transactions`),
        axios.get(`${API}/admin/refunds`),
      ]);
      setSettings(s.data);
      setTransactions(t.data?.transactions || []);
      setRefunds(r.data?.refunds || []);
    } catch (e) {
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const issueRefund = async () => {
    if (!refundFor) return;
    setRefunding(true);
    try {
      const payload = { transaction_id: refundFor.session_id, reason: refundReason };
      if (refundAmount) payload.amount = parseFloat(refundAmount);
      const res = await axios.post(`${API}/admin/refunds`, payload);
      toast.success(`Refund issued (${res.data.refund.amount} ${res.data.refund.currency?.toUpperCase()})`);
      setRefundFor(null);
      setRefundAmount("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  const fmt = (n, ccy = "usd") => `${(n || 0).toFixed(2)} ${ccy?.toUpperCase()}`;

  return (
    <div className="space-y-8" data-testid="admin-payments">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#002FA7]" /> Payments
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Where your earnings are paid out and tools to manage refunds.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-3 py-2 border border-zinc-800 rounded"
          data-testid="payments-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Payout settings card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6" data-testid="payout-settings">
        <div className="flex items-center gap-2 mb-4">
          <Banknote className="w-4 h-4 text-zinc-400" />
          <h2 className="text-base font-semibold text-white">Payout destination</h2>
          {settings?.is_test_mode && (
            <span className="text-[10px] uppercase tracking-wide bg-amber-700 text-amber-100 px-1.5 py-0.5 rounded">TEST MODE</span>
          )}
        </div>
        {!settings ? (
          <div className="h-20 bg-zinc-800/40 rounded animate-pulse" />
        ) : settings.error ? (
          <div className="bg-amber-900/30 border border-amber-800 rounded p-3 flex gap-2 text-amber-200 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>Could not read your Stripe account: {settings.error}</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Detail label="Provider" value={(settings.provider || "—").toUpperCase()} />
            <Detail label="API Key" value={settings.key_prefix} mono />
            <Detail label="Account Email" value={settings.account_email || "(unknown)"} />
            <Detail label="Business Name" value={settings.business_name || "(none)"} />
            <Detail label="Country" value={(settings.country || "—").toUpperCase()} />
            <Detail label="Default Currency" value={(settings.default_currency || "—").toUpperCase()} />
            <Detail
              label="Payouts Enabled"
              value={settings.payouts_enabled === true ? "Yes" : settings.payouts_enabled === false ? "No" : "Unknown"}
              tone={settings.payouts_enabled ? "green" : settings.payouts_enabled === false ? "red" : "neutral"}
            />
          </div>
        )}
        <div className="mt-5 pt-5 border-t border-zinc-800">
          <p className="text-xs text-zinc-500">
            Payout banking details (where Stripe deposits your earnings) are configured inside the Stripe Dashboard for security reasons.
          </p>
          <a
            href={settings?.dashboard_url || "https://dashboard.stripe.com/settings/payouts"}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-[#3b82f6] hover:underline mt-2"
            data-testid="open-stripe-dashboard"
          >
            Open Stripe payout settings <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Transactions table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden" data-testid="transactions-table">
        <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
          <Receipt className="w-4 h-4 text-zinc-400" />
          <h2 className="text-base font-semibold text-white">Recent transactions ({transactions.length})</h2>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/50">
                <tr className="text-zinc-400 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Plan</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {transactions.map((t) => (
                  <tr key={t.session_id} className="text-zinc-200" data-testid={`tx-${t.session_id}`}>
                    <td className="px-4 py-2 tabular-nums text-zinc-400 text-xs">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2">
                      <div className="text-zinc-100">{t.user_name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{t.user_email || ""}</div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(t.amount, t.currency)}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        t.payment_status === "paid" ? "bg-emerald-700 text-white" :
                        t.payment_status === "refunded" ? "bg-red-700 text-white" :
                        "bg-zinc-700 text-zinc-300"
                      }`}>
                        {t.payment_status || "?"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-zinc-400">{t.plan_id || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      {t.payment_status === "paid" ? (
                        <button
                          onClick={() => { setRefundFor(t); setRefundAmount(""); setRefundReason("requested_by_customer"); }}
                          className="text-xs px-2.5 py-1 border border-red-800 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded"
                          data-testid={`refund-btn-${t.session_id}`}
                        >
                          Refund
                        </button>
                      ) : (
                        <span className="text-[11px] text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Refunds list */}
      {refunds.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden" data-testid="refunds-table">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-base font-semibold text-white">Refunds issued ({refunds.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/50">
                <tr className="text-zinc-400 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Stripe ID</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-left px-4 py-2">Issued by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {refunds.map((r) => (
                  <tr key={r.refund_id} className="text-zinc-200">
                    <td className="px-4 py-2 tabular-nums text-zinc-400 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-zinc-400">{r.stripe_refund_id}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(r.amount, r.currency)}</td>
                    <td className="px-4 py-2 text-xs">{r.reason || "—"}</td>
                    <td className="px-4 py-2 text-xs text-zinc-400">{r.issued_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Refund modal */}
      {refundFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !refunding && setRefundFor(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="refund-modal">
            <h3 className="text-lg font-semibold text-white mb-1">Issue refund</h3>
            <p className="text-xs text-zinc-400 mb-4">
              Refunding <span className="font-mono">{refundFor.session_id?.slice(0, 24)}…</span> for {fmt(refundFor.amount, refundFor.currency)} ({refundFor.user_email})
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wide text-zinc-400">Reason</label>
                <select
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100"
                  data-testid="refund-reason"
                >
                  <option value="requested_by_customer">Requested by customer</option>
                  <option value="duplicate">Duplicate charge</option>
                  <option value="fraudulent">Fraudulent</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-zinc-400">
                  Amount (leave blank for full refund)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={refundFor.amount}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={`${refundFor.amount}`}
                  className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100"
                  data-testid="refund-amount"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setRefundFor(null)}
                disabled={refunding}
                className="px-3 py-2 text-sm text-zinc-300 hover:text-white border border-zinc-700 rounded"
                data-testid="refund-cancel"
              >
                Cancel
              </button>
              <button
                onClick={issueRefund}
                disabled={refunding}
                className="px-3 py-2 text-sm bg-red-700 hover:bg-red-800 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="refund-confirm"
              >
                {refunding ? <><Loader2 className="w-4 h-4 animate-spin" /> Refunding…</> : "Confirm refund"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value, tone = "neutral", mono = false }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
    <p className={`mt-0.5 ${mono ? "font-mono" : ""} ${
      tone === "green" ? "text-emerald-400" : tone === "red" ? "text-red-400" : "text-zinc-100"
    }`}>{value}</p>
  </div>
);

export default AdminPayments;
